import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  ClientDepartment,
  ClientDepartmentStatus,
  ClientLocation,
  ClientLocationStatus,
  useCreateClientDepartmentMutation,
  useUpdateClientDepartmentMutation,
  useCreateClientLocationMutation,
  useUpdateClientLocationMutation,
} from 'api';
import { EditableList, EditableListType } from 'features/admin/types';
import {
  useCreateDepartment,
  useCreateLocation,
  useUpdateDepartment,
  useUpdateLocation,
} from 'features/admin/updaters';
import { useNotify } from 'hooks';

import Create from './Create';
import Update from './Update';

interface IProps {
  list: Array<ClientDepartment | ClientLocation>;
  listType: EditableList;
}

const useStyles = makeStyles(theme => ({
  spacer: {
    height: theme.spacing(2),
  },
}));

const DepartmentsLocationsIndex: FC<IProps> = ({ list, listType }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const classes = useStyles();

  const createClientDepartmentOptions = useCreateDepartment();
  const { mutateAsync: createClientDepartment } = useCreateClientDepartmentMutation({
    onSuccess: handleCompleted,
    ...createClientDepartmentOptions,
  });

  const createClientLocationOptions = useCreateLocation();
  const { mutateAsync: createClientLocation } = useCreateClientLocationMutation({
    onSuccess: handleCompleted,
    ...createClientLocationOptions,
  });

  const updateClientDepartmentOptions = useUpdateDepartment();
  const { mutateAsync: updateClientDepartment } = useUpdateClientDepartmentMutation({
    onSuccess: handleCompleted,
    ...updateClientDepartmentOptions,
  });

  const updateClientLocationOptions = useUpdateLocation();
  const { mutateAsync: updateClientLocation } = useUpdateClientLocationMutation({
    onSuccess: handleCompleted,
    ...updateClientLocationOptions,
  });

  function getDepartmentsOptions() {
    return {
      createAsyncMutation: createClientDepartment,
      updateAsyncMutation: updateClientDepartment,
      activeDepartmentStatus: { department_status: ClientDepartmentStatus.Active },
      disabledDepartmentStatus: { department_status: ClientDepartmentStatus.Disabled },
    };
  }

  function getLocationsOptions() {
    return {
      createAsyncMutation: createClientLocation,
      updateAsyncMutation: updateClientLocation,
      activeLocationStatus: { location_status: ClientLocationStatus.Active },
      disabledLocationStatus: { location_status: ClientLocationStatus.Disabled },
    };
  }

  function handleCompleted() {
    const message = t('components:savedSuccessfully');
    notify.info({ message });
  }

  const isDepartments = listType === EditableListType.Departments;
  const options = isDepartments ? getDepartmentsOptions() : getLocationsOptions();

  return (
    <Fragment>
      <Create
        titleKey={`admin:${listType}`}
        inputPlaceholderKey={`admin:${listType}AddPlaceholder`}
        {...options}
      />
      <div className={classes.spacer} />
      <Grid container alignItems="center" spacing={3}>
        <Grid item xs={3} />
        <Grid item xs={7}>
          <Grid container direction="column" spacing={2}>
            {list.map(listItem => (
              <Update
                key={listItem.id}
                item={listItem}
                inputPlaceholderKey={`admin:${listType}AddPlaceholder`}
                confirmDeleteKey={`admin:${listType}DeleteAlert`}
                {...options}
              />
            ))}
          </Grid>
        </Grid>
        <Grid item xs={2} />
      </Grid>
    </Fragment>
  );
};

export default DepartmentsLocationsIndex;
